<template>
    <div class="w-768">
        <multi-lang-toggle :form="form"></multi-lang-toggle>

        <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs" class="w-full mb-32">
            <div v-for="lang in languages" :key="lang">
                <simple-input
                    v-show="lang === selectedLanguage"
                    v-model="form.title[lang]"
                    emojis
                    class="mb-16"
                >
                    {{ $t('form.title') }} <span v-if="languages.length > 1">({{ lang }})</span>
                </simple-input>

              <wysiwyg
                  v-show="lang === selectedLanguage"
                  v-model="form.description[lang]"
                  full-featured
              >
                {{ $t('form.description') }}
                <span v-if="languages.length > 1">({{ lang }})</span>
              </wysiwyg>
            </div>
        </translatable>

        <div v-if="fetchingItems">
            <loading></loading>
        </div>

        <div v-else>
            <h3 class="first-letter:uppercase text-body font-semibold leading-none mb-16">
                {{ $t('components.added_items') }}
            </h3>

            <draggable
                v-bind="dragOptions"
                v-if="chosenItems.length > 0"
                v-model="chosenItems"
                handle=".draggable"
                item-key="id"
                class="flex w-full p-4 border-solid border border-primary-20 rounded flex-wrap w-full mb-16"
                @start="drag = true"
                @end="drag = false"
            >
                <template #item="{ element }">
                    <div class="w-1/4 p-4 flex-none h-192 inline-block">
                        <div
                            class="h-full rounded border-2 border-primary-20 bg-white p-16 flex flex-col"
                        >
                            <div class="text-center mt-8 truncate font-normal">
                                <span
                                    v-if="element.label[locale]">
                                    {{ element.label[locale]}}
                                </span>
                                <span
                                    v-else
                                    class="text-primary-40">
                                    {{ $t('form.label') }}
                                </span>
                                     :
                                <span
                                    v-if="element.value[locale]">
                                    {{ element.value[locale] }}
                                </span>
                                <span
                                    v-else
                                    class="text-primary-40">
                                    {{ $t('form.value') }}
                                </span>
                            </div>
                        </div>

                        <div class="flex items-center justify-center gap-8 -mt-64">
                            <simple-button
                                icon="edit"
                                look="icon"
                                @click="edit(element)"
                            />
                            <simple-button
                                icon="link-off"
                                look="icon"
                                @click="toggleItem(element.id)"
                            />
                            <simple-button
                                class="draggable !cursor-grab"
                                display="neutral"
                                icon="drag-indicator"
                                look="icon"
                            />
                        </div>
                    </div>
                </template>
            </draggable>

            <div v-else class="mb-16 italic first-letter:uppercase">
                {{ $t('components.no_items_added') }}
            </div>

            <simple-button
                icon="add-circle"
                @click="create"
                class="mb-32">
                {{ $t('components.add_item') }}
            </simple-button>

            <h3 class="first-letter:uppercase text-body font-semibold leading-none mb-16">
                {{ $t('components.all_items') }}
            </h3>

            <div
                v-if="items.length > 0"
                class="overflow-auto p-16 border-solid border border-primary-20 rounded w-full mb-16">
                <!-- <div class="flex uppercase text-body-sm font-semibold text-primary-80 border-b-2 border-primary-20 mb-24">
                    {{ $t('site_editor.facts.title') }}
                </div> -->
                <div
                    class="flex items-center"
                    v-for="(item, index) in items"
                    :class="(index != items.length - 1) ? 'mb-16' : ''"
                    :key="item.id"
                >
                    <div class="flex-grow truncate cursor-pointer" @click="toggleItem(item.id)">
                        <span v-if="item.label[locale]">
                            {{ item.label[locale]}}
                        </span>
                        <span v-else>
                            {{ $t('form.label') }}
                        </span>:
                        <span v-if="item.value[locale]">
                            {{ item.value[locale] }}
                        </span>
                        <span v-else>
                            {{ $t('form.value') }}
                        </span>
                    </div>
                    <button class="mr-16" @click="toggleItem(item.id)">
                        <svg-icon type="add" class="max-w-16 max-h-16"></svg-icon>
                    </button>
                    <button class="mr-16" @click="edit(item)">
                        <svg-icon type="edit" class="max-w-16 max-h-16"></svg-icon>
                    </button>
                    <button class="mr-8" @click="confirmDelete(item)">
                        <svg-icon type="delete" class="max-w-16 max-h-16"></svg-icon>
                    </button>
                </div>
            </div>

            <div v-else class="mb-16 italic first-letter:uppercase">
                {{ $t('components.no_items_available') }}
            </div>
        </div>

        <edit-item v-if="editing" :fact="editing" @close="cancelEdit()"></edit-item>
        <edit-item v-if="creating" @close="cancelCreate()" @created="toggleItem($event)"></edit-item>

        <popup-modal-confirm
            v-if="deleting"
            :message="$t('general.confirm_delete_subject', {subject: ''})"
            :confirmText="$t('general.delete_subject', {subject: ''})"
            :working="performingDeleteRequest"
            @confirm="deleteItem(deleting.id)"
            @close="cancelDelete"
        >
        </popup-modal-confirm>

    </div>
</template>

<script>
import SvgIcon from './SvgIcon.vue';
import {mapState} from 'pinia';
import {useMainStore} from '../../state/store';
import Loading from '../../components/ui/Loading.vue';
import Item from '../../models/Fact';
import * as items from '../../services/FactService';
import PopupModalConfirm from './PopupModalConfirm.vue';
import EditItem from './EditFact.vue';
import draggable from 'vuedraggable';
import { draggableItemsForm } from '../../mixins/draggableItemsForm';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import Translatable from '../forms/Translatable.vue';
import Input from '../forms/Input.vue';
import LanguagesMultiToggle from '../forms/LanguagesMultiToggle.vue';
import wysiwyg from "../forms/Wysiwyg.vue";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

export default {
    components: {
      wysiwyg,
        'loading': Loading,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'edit-item': EditItem,
        'draggable': draggable,
        'translatable': Translatable,
        'simple-button': SimpleButton,
        'simple-input': Input,
        'multi-lang-toggle': LanguagesMultiToggle,
    },
    mixins: [draggableItemsForm(items, Item)],
    props: {
        form: {
            required: true,
        }
    },
    data: function () {
        return {
            fetchingItems: true,
            deleting: null,
            performingDeleteRequest: false,
            editing: null,
            creating: false,
            sortOrder: [],
            drag: false
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.title[languages[index]] && this.form.data.published[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        dragOptions () {
            return {
                animation: 200,
                group: "items",
                disabled: false,
                ghostClass: "ghost"
            };
        }
    },
}
</script>
